/**
 * ApprovalModal.js
 *
 * Reusable Approval Modal Component using React DOM Portal
 * Handles all 3 approval actions: View, Approve, Reject in a single modal
 *
 * Props:
 * - modal: Object { type: 'view'|'approve'|'reject', data: approvalObject }
 * - onClose: Callback when modal closes
 * - onApprove: Callback for approve action (approvalId, reason)
 * - onReject: Callback for reject action (approvalId, reason)
 */

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { DeviceIDBadge } from '../../../shared/components/common/Badges';
import ApprovalStatusBadge from '../../dashboard/widgets/Tables/ApprovalStatusBadge';
import { formatCurrency, formatCurrencyDynamic } from '../../../utils/numberUtils';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import ModalCloseButton from '../../../shared/components/modals/ModalCloseButton';
import './ApprovalModal.css';

const ApprovalModal = ({ modal, onClose, onApprove, onReject, notification, onDismissNotification, token, user }) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [deviceDetails, setDeviceDetails] = useState(null);
  const [selectedDocumentForPreview, setSelectedDocumentForPreview] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showDocumentPreviewModal, setShowDocumentPreviewModal] = useState(false);
  const { type, data } = modal || {};

  // Reset state when modal opens/closes
  useEffect(() => {
    if (modal) {
      // Only reset if modal is newly opened (different device/type)
      // Don't reset on every render to preserve showRejectForm state
      setReason('');
      setSubmitting(false);
      // Don't reset showRejectForm here - preserve form state during interaction
      // setShowRejectForm(false);

      // Fetch documents and device details if device exists and token is available
      if (data?.device_id && token) {
        fetchDocuments(data.device_id);
        // Also fetch full device details to ensure we have notes
        fetchDeviceDetails(data.device_id);
      }
    } else {
      // Reset state when modal closes
      setDeviceDetails(null);
      setDocuments([]);
      setShowRejectForm(false);
      setReason('');
    }
  }, [modal?.data?.approval_id]);

  // Fetch documents for the device
  const fetchDocuments = async (deviceId) => {
    try {
      setDocumentsLoading(true);
      const response = await axios.get(
        API_ENDPOINTS.DEVICES.DOCUMENTS(deviceId),
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setDocuments([]);
    } finally {
      setDocumentsLoading(false);
    }
  };

  // Fetch full device details to get notes (if not already in approval data)
  const fetchDeviceDetails = async (deviceId) => {
    try {
      const response = await axios.get(
        API_ENDPOINTS.DEVICES.GET(deviceId),
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (response.data) {
        setDeviceDetails(response.data);
      }
    } catch (error) {
      console.error('Error fetching device details:', error);
      setDeviceDetails(null);
    }
  };

  // Handle document preview
  const handleDocumentPreview = async (doc) => {
    setSelectedDocumentForPreview(doc);
    setShowDocumentPreviewModal(true);
    setPreviewLoading(true);

    try {
      const response = await axios.get(
        API_ENDPOINTS.DOCUMENTS.GET(data.device_id, doc.id),
        {
          headers: { 'Authorization': `Bearer ${token}` },
          responseType: doc.mime_type.includes('pdf') ? 'blob' : 'arraybuffer'
        }
      );

      const blob = new Blob([response.data], { type: doc.mime_type });
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (error) {
      console.error('Error previewing document:', error);
      setSelectedDocumentForPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Handle document download
  const handleDocumentDownload = async (docId, filename) => {
    try {
      const response = await axios.get(
        API_ENDPOINTS.DOCUMENTS.GET(data.device_id, docId),
        {
          headers: { 'Authorization': `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  };

  // Close document preview modal
  const closeDocumentPreviewModal = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setShowDocumentPreviewModal(false);
    setSelectedDocumentForPreview(null);
    setPreviewUrl(null);
  };

  // Handle keyboard escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && modal) {
        onClose();
      }
    };

    if (modal) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [modal, onClose]);

  if (!modal || !data) return null;

  const getReviewerDisplayName = () => {
    if (!data.reviewed_by_employee_id) return null;
    if (user?.employee_id && data.reviewed_by_employee_id === user.employee_id) {
      return 'You';
    }
    return data.reviewed_by_name || data.reviewed_by_employee_id || 'Unknown';
  };

  const reviewerDisplayName = getReviewerDisplayName();
  const reviewerLabel =
    data.approval_status === 'rejected'
      ? 'Rejected By:'
      : data.approval_status === 'approved'
        ? 'Approved By:'
        : null;

  const handleSubmit = async () => {
    if (submitting) return;

    try {
      setSubmitting(true);
      if (type === 'approve') {
        await onApprove(data.approval_id, reason);
      } else if (type === 'reject') {
        await onReject(data.approval_id, reason);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Modal Content
  const modalContent = (
    <div className="approval-modal-overlay">
      <div
        className="approval-modal-content"
      >
        {/* Modal Header */}
        <div className="approval-modal-header">
          <h2>
            {type === 'view'
              ? `📋 Device Details`
              : type === 'approve'
              ? '✅ Approve Device'
              : '❌ Reject Device'}
          </h2>
          <ModalCloseButton onClick={onClose} title="Close (Esc)" />
        </div>

        {/* Notification Banner */}
        {notification?.show && (
          <div className={`approval-notification approval-notification-${notification.type}`}>
            <div className="approval-notification-content">
              <span className="approval-notification-icon">
                {notification.type === 'success' ? '✓' : '⚠'}
              </span>
              <span className="approval-notification-message">
                {notification.message}
              </span>
            </div>
            <button
              className="approval-notification-close"
              onClick={onDismissNotification}
              title="Dismiss"
            >
              ✕
            </button>
          </div>
        )}

        {/* Modal Body */}
        <div className="approval-modal-body">
          {/* Device Details Section */}
          <div className="approval-device-details">
            <h3>Basic Device Information</h3>
            <div className="approval-details-grid">
              <div className="approval-detail-row">
                <label>Device ID:</label>
                <DeviceIDBadge deviceId={data.device_identifier || 'N/A'} size="small" />
              </div>
              <div className="approval-detail-row">
                <label>Device Name:</label>
                <span>{data.device_name || 'N/A'}</span>
              </div>
              <div className="approval-detail-row">
                <label>Model:</label>
                <span>{data.device_data?.model || 'N/A'}</span>
              </div>
              <div className="approval-detail-row">
                <label>Serial Number:</label>
                <span>{data.device_data?.serial_number || 'N/A'}</span>
              </div>
              <div className="approval-detail-row">
                <label>Year of Manufacture:</label>
                <span>{data.device_data?.year_of_manufacture || 'N/A'}</span>
              </div>
              <div className="approval-detail-row">
                <label>Manufacturer:</label>
                <span>{data.device_data?.manufacturer || 'N/A'}</span>
              </div>
              <div className="approval-detail-row">
                <label>Supplier:</label>
                <span>{data.device_data?.supplier || 'N/A'}</span>
              </div>
              <div className="approval-detail-row">
                <label>Department:</label>
                <span>{data.department || 'N/A'}</span>
              </div>
              <div className="approval-detail-row">
                <label>Custodian:</label>
                <span>{data.device_data?.custodian_name || 'N/A'}</span>
              </div>
              <div className="approval-detail-row">
                <label>Physical Location:</label>
                <span>{data.device_data?.physical_location || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Cost & Installation Information */}
          <div className="approval-device-details">
            <h3>Cost & Installation Details</h3>
            <div className="approval-details-grid">
              <div className="approval-detail-row">
                <label>Product Cost:</label>
                <span>{formatCurrency(data.device_data.product_cost)} <strong>({formatCurrencyDynamic(data.device_data.product_cost)})</strong></span>
              </div>
              <div className="approval-detail-row">
                <label>Installation Date:</label>
                <span>{data.device_data?.installation_date ? new Date(data.device_data.installation_date).toLocaleDateString() : 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Warranty & Service Contract */}
          <div className="approval-device-details">
            <h3>Warranty & Service Contract</h3>
            <div className="approval-details-grid">
              <div className="approval-detail-row">
                <label>Service Contract Type:</label>
                <span>{data.device_data?.service_contract_type || 'N/A'}</span>
              </div>
              {data.device_data?.service_contract_type === 'Warranty' && (
                <>
                  <div className="approval-detail-row">
                    <label>Warranty Period (Years):</label>
                    <span>{data.device_data?.warranty_period || 'N/A'}</span>
                  </div>
                  <div className="approval-detail-row">
                    <label>Warranty Expiry Date:</label>
                    <span>{data.device_data?.warranty_expiry ? new Date(data.device_data.warranty_expiry).toLocaleDateString() : 'N/A'}</span>
                  </div>
                </>
              )}
              {(data.device_data?.service_contract_type === 'LAMC' || data.device_data?.service_contract_type === 'CAMC') && (
                <>
                  <div className="approval-detail-row">
                    <label>Service Contract Period (Years):</label>
                    <span>{data.device_data?.service_contract_period || 'N/A'}</span>
                  </div>
                  <div className="approval-detail-row">
                    <label>Service Contract Cost:</label>
                    <span>₹{data.device_data?.service_contract_cost ? parseFloat(data.device_data.service_contract_cost).toLocaleString('en-IN') : 'N/A'}</span>
                  </div>
                  <div className="approval-detail-row">
                    <label>Contract Start Date:</label>
                    <span>{data.device_data?.contract_start_date ? new Date(data.device_data.contract_start_date).toLocaleDateString() : 'N/A'}</span>
                  </div>
                  <div className="approval-detail-row">
                    <label>Contract Expiry Date:</label>
                    <span>{data.device_data?.contract_expiry ? new Date(data.device_data.contract_expiry).toLocaleDateString() : 'N/A'}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* PPM Information */}
          {data.device_data?.ppm_frequency && (
            <div className="approval-device-details">
              <h3>PPM (Planned Preventive Maintenance)</h3>
              <div className="approval-details-grid">
                <div className="approval-detail-row">
                  <label>PPM Frequency:</label>
                  <span>{data.device_data?.ppm_frequency || 'N/A'}</span>
                </div>
                <div className="approval-detail-row">
                  <label>PPM Last Done Date:</label>
                  <span>{data.device_data?.ppm_last_done_date ? new Date(data.device_data.ppm_last_done_date).toLocaleDateString() : 'N/A'}</span>
                </div>
                <div className="approval-detail-row">
                  <label>PPM Next Due Date:</label>
                  <span>{data.device_data?.ppm_next_due_date ? new Date(data.device_data.ppm_next_due_date).toLocaleDateString() : 'N/A'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Calibration Information */}
          {data.device_data?.calibration_required === 'Yes' && (
            <div className="approval-device-details">
              <h3>Calibration / Validation</h3>
              <div className="approval-details-grid">
                <div className="approval-detail-row">
                  <label>Calibration Required:</label>
                  <span>{data.device_data?.calibration_required || 'N/A'}</span>
                </div>
                <div className="approval-detail-row">
                  <label>Calibration Frequency:</label>
                  <span>{data.device_data?.calibration_frequency || 'N/A'}</span>
                </div>
                <div className="approval-detail-row">
                  <label>Calibration Last Done Date:</label>
                  <span>{data.device_data?.calibration_last_done_date ? new Date(data.device_data.calibration_last_done_date).toLocaleDateString() : 'N/A'}</span>
                </div>
                <div className="approval-detail-row">
                  <label>Calibration Next Due Date:</label>
                  <span>{data.device_data?.calibration_next_due_date ? new Date(data.device_data.calibration_next_due_date).toLocaleDateString() : 'N/A'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Regulatory Compliance */}
          {data.device_data?.compliance_required === 'Yes' && (
            <div className="approval-device-details">
              <h3>Regulatory Compliance</h3>
              <div className="approval-details-grid">
                <div className="approval-detail-row">
                  <label>Compliance Required:</label>
                  <span>{data.device_data?.compliance_required || 'N/A'}</span>
                </div>
                <div className="approval-detail-row">
                  <label>Compliance Type:</label>
                  <span>{data.device_data?.compliance_type || 'N/A'}</span>
                </div>
                <div className="approval-detail-row">
                  <label>Compliance Frequency:</label>
                  <span>{data.device_data?.compliance_frequency || 'N/A'}</span>
                </div>
                <div className="approval-detail-row">
                  <label>Compliance Last Done Date:</label>
                  <span>{data.device_data?.compliance_last_done_date ? new Date(data.device_data.compliance_last_done_date).toLocaleDateString() : 'N/A'}</span>
                </div>
                <div className="approval-detail-row">
                  <label>Compliance Next Due Date:</label>
                  <span>{data.device_data?.compliance_next_due_date ? new Date(data.device_data.compliance_next_due_date).toLocaleDateString() : 'N/A'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Technician Assignment */}
          {data.technicians && (data.technicians.primary_technician_name || data.technicians.secondary_technician_name) && (
            <div className="approval-device-details">
              <h3>Technician Assignment</h3>
              <div className="approval-details-grid">
                {data.technicians.primary_technician_name && (
                  <div className="approval-detail-row">
                    <label>Primary Technician:</label>
                    <span>{data.technicians.primary_technician_name}</span>
                  </div>
                )}
                {data.technicians.secondary_technician_name && (
                  <div className="approval-detail-row">
                    <label>Secondary Technician:</label>
                    <span>{data.technicians.secondary_technician_name}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Additional Notes */}
          {(data.device_data?.notes || deviceDetails?.notes) && (
            <div className="approval-device-details">
              <h3>Additional Notes</h3>
              <p className="approval-notes-text">
                {data.device_data?.notes || deviceDetails?.notes}
              </p>
            </div>
          )}

          {/* Documents & Attachments */}
          {token && data?.device_id && (
            <div className="approval-device-details">
              <h3>📎 Documents & Attachments</h3>
              {documentsLoading ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  <span style={{ fontSize: '1.2rem' }}>⟳ Loading documents...</span>
                </div>
              ) : documents.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', backgroundColor: '#f8f9fa', borderRadius: '4px', color: '#666' }}>
                  <p style={{ margin: '0' }}>No documents uploaded for this device.</p>
                </div>
              ) : (
                <div style={{ padding: '0' }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr auto auto auto',
                    gap: '12px',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: '1px solid #e9ecef'
                  }}>
                    <div style={{ fontWeight: '600', color: '#666', minWidth: '80px' }}>Type</div>
                    <div style={{ fontWeight: '600', color: '#666' }}>File Name</div>
                    <div style={{ fontWeight: '600', color: '#666', minWidth: '60px', textAlign: 'right' }}>Size</div>
                    <div style={{ fontWeight: '600', color: '#666', minWidth: '80px', textAlign: 'right' }}>Date</div>
                    <div style={{ fontWeight: '600', color: '#666', minWidth: '70px', textAlign: 'center' }}>Actions</div>
                  </div>
                  {documents.map((doc) => (
                    <div
                      key={doc.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'auto 1fr auto auto auto',
                        gap: '12px',
                        alignItems: 'center',
                        padding: '12px 8px',
                        borderBottom: '1px solid #f0f0f0',
                        fontSize: '0.9rem'
                      }}
                    >
                      <div style={{ minWidth: '80px', color: 'var(--dp-accent-primary)' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 8px',
                          backgroundColor: '#f0f4ff',
                          borderRadius: '3px',
                          fontSize: '0.8rem',
                          fontWeight: '500'
                        }}>
                          {doc.document_type === 'manual' && 'Manual'}
                          {doc.document_type === 'certificate' && 'Certificate'}
                          {doc.document_type === 'warranty' && 'Warranty'}
                          {doc.document_type === 'purchase_order' && 'PO'}
                          {doc.document_type === 'maintenance_report' && 'Maintenance'}
                          {doc.document_type === 'calibration' && 'Calibration'}
                          {doc.document_type === 'safety' && 'Safety'}
                          {doc.document_type === 'other' && 'Other'}
                        </span>
                      </div>
                      <div style={{ color: '#333' }}>
                        <span style={{
                          marginRight: '8px',
                          fontSize: '1.1rem'
                        }}>
                          {doc.mime_type.includes('pdf') && '📄'}
                          {doc.mime_type.includes('image') && '🖼️'}
                          {doc.mime_type.includes('word') && '📝'}
                          {doc.mime_type.includes('excel') || doc.mime_type.includes('spreadsheet') ? '📊' : ''}
                          {!doc.mime_type.includes('pdf') && !doc.mime_type.includes('image') && !doc.mime_type.includes('word') && !doc.mime_type.includes('excel') && !doc.mime_type.includes('spreadsheet') && '📎'}
                        </span>
                        {doc.file_name}
                      </div>
                      <div style={{ color: '#666', textAlign: 'right', minWidth: '60px' }}>
                        {doc.file_size < 1024 ? doc.file_size + ' B' : doc.file_size < 1048576 ? (doc.file_size / 1024).toFixed(1) + ' KB' : (doc.file_size / 1048576).toFixed(1) + ' MB'}
                      </div>
                      <div style={{ color: '#666', minWidth: '80px', textAlign: 'right' }}>
                        {new Date(doc.created_at).toLocaleDateString()}
                      </div>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', minWidth: '70px' }}>
                        <button
                          onClick={() => handleDocumentPreview(doc)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1.1rem',
                            padding: '4px',
                            transition: 'transform 0.2s'
                          }}
                          title="Preview"
                          onMouseEnter={(e) => e.target.style.transform = 'scale(1.2)'}
                          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                        >
                          👁️
                        </button>
                        <button
                          onClick={() => handleDocumentDownload(doc.id, doc.file_name)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1.1rem',
                            padding: '4px',
                            transition: 'transform 0.2s'
                          }}
                          title="Download"
                          onMouseEnter={(e) => e.target.style.transform = 'scale(1.2)'}
                          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                        >
                          ⬇️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Approval Information */}
          <div className="approval-device-details">
            <h3>Approval Information</h3>
            <div className="approval-details-grid">
              <div className="approval-detail-row">
                <label>Requested By:</label>
                <span>{data.manager_name || 'Unknown'}</span>
              </div>
              <div className="approval-detail-row">
                <label>Requested At:</label>
                <span>{new Date(data.requested_at).toLocaleString()}</span>
              </div>
              <div className="approval-detail-row">
                <label>Status:</label>
                <span>
                  {data.approval_status ? (
                    <ApprovalStatusBadge status={data.approval_status} size="medium" />
                  ) : (
                    'N/A'
                  )}
                </span>
              </div>
              <div className="approval-detail-row">
                <label>Resubmission Count:</label>
                <span>{data.resubmission_count || 0}</span>
              </div>
              {reviewerLabel && reviewerDisplayName && (
                <div className="approval-detail-row">
                  <label>{reviewerLabel}</label>
                  <span>
                    {reviewerDisplayName === 'You' ? (
                      <span className="doc-uploader-you" title="Reviewed by you">
                        You
                      </span>
                    ) : (
                      reviewerDisplayName
                    )}
                  </span>
                </div>
              )}
              {(data.approval_status === 'approved' || data.approval_status === 'rejected') && data.reviewed_at && (
                <div className="approval-detail-row">
                  <label>Reviewed At:</label>
                  <span>{new Date(data.reviewed_at).toLocaleString()}</span>
                </div>
              )}
            </div>
          </div>

          {/* Saved decision reason when viewing a completed approval */}
          {data.approval_status === 'rejected' && (
            <div className="approval-device-details">
              <h3>Rejection Reason</h3>
              <p className="approval-notes-text">
                {data.decision_reason?.trim() || 'No reason provided'}
              </p>
            </div>
          )}
          {data.approval_status === 'approved' && data.decision_reason?.trim() && (
            <div className="approval-device-details">
              <h3>Approval Comments</h3>
              <p className="approval-notes-text">
                {data.decision_reason}
              </p>
            </div>
          )}

          {/* Action Specific Content */}
          {type === 'approve' && (
            <div className="approval-action-section">
              <h3>Approval Details</h3>
              <div className="approval-form-group">
                <label htmlFor="approval-reason">
                  Approval Comments (Optional):
                </label>
                <textarea
                  id="approval-reason"
                  placeholder="Add any comments or verification notes..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows="4"
                />
              </div>
            </div>
          )}

          {type === 'reject' && (
            <div className="approval-action-section">
              <h3>Rejection Details</h3>
              <div className="approval-form-group">
                <label htmlFor="rejection-reason">
                  Rejection Reason (Required):
                </label>
                <textarea
                  id="rejection-reason"
                  placeholder="Please explain in detail why this device is being rejected. Include any issues, remarks, or notes for the manager to understand what corrections are needed..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows="4"
                  required
                />
                <small className="approval-helper-text">
                  The manager will be notified and can edit the device to correct the issues. This will be stored in the audit trail.
                </small>
              </div>
            </div>
          )}

          {/* Rejection Form when clicked from View mode */}
          {type === 'view' && showRejectForm && (
            <div className="approval-action-section">
              <h3>Rejection Details</h3>
              <div className="approval-form-group">
                <label htmlFor="rejection-reason-inline">
                  Rejection Reason (Required):
                </label>
                <textarea
                  id="rejection-reason-inline"
                  placeholder="Please explain in detail why this device is being rejected. Include any issues, remarks, or notes for the manager to understand what corrections are needed..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows="4"
                  required
                  autoFocus
                />
                <small className="approval-helper-text">
                  The manager will be notified and can edit the device to correct the issues. This will be stored in the audit trail.
                </small>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="approval-modal-footer">
          <button
            type="button"
            className="action-btn action-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Close
          </button>

          {/* For View Mode with Pending Status - Show Approve/Reject Options */}
          {type === 'view' && data?.approval_status === 'pending' && !showRejectForm && (
            <>
              <button
                type="button"
                className="action-btn action-reject"
                onClick={() => {
                  setShowRejectForm(true);
                  setReason('');
                }}
                disabled={submitting || notification?.show}
              >
                {submitting ? 'Processing...' : '⚠️ Reject & Notify'}
              </button>
              <button
                type="button"
                className="action-btn action-approve"
                onClick={() => {
                  if (onApprove) {
                    onApprove(data.approval_id, '');
                  }
                }}
                disabled={submitting || notification?.show}
              >
                {submitting ? 'Processing...' : '✅ Approve Device'}
              </button>
            </>
          )}

          {/* For View Mode when Reject Form is Shown */}
          {type === 'view' && showRejectForm && (
            <>
              <button
                type="button"
                className="action-btn action-secondary modal-footer-back"
                onClick={() => {
                  setShowRejectForm(false);
                  setReason('');
                }}
                disabled={submitting || notification?.show}
              >
                Back
              </button>
              <button
                type="button"
                className="action-btn action-reject"
                onClick={() => {
                  if (reason.trim() && onReject) {
                    onReject(data.approval_id, reason);
                  }
                }}
                disabled={submitting || notification?.show || !reason.trim()}
              >
                {submitting ? 'Processing...' : '⚠️ Confirm Rejection'}
              </button>
            </>
          )}

          {/* For Actual Approve/Reject Form Modes */}
          {(type === 'approve' || type === 'reject') && (
            <button
              type="button"
              className={`action-btn action-${type === 'approve' ? 'approve' : 'reject'}`}
              onClick={handleSubmit}
              disabled={submitting || notification?.show || (type === 'reject' && !reason.trim())}
            >
              {submitting
                ? 'Processing...'
                : type === 'approve'
                ? '✅ Approve Device'
                : '⚠️ Reject & Notify'}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Document Preview Modal
  const documentPreviewModal = showDocumentPreviewModal && selectedDocumentForPreview && (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
      }}
      onClick={closeDocumentPreviewModal}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '90%',
          maxWidth: '1000px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Preview Modal Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #e9ecef',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ margin: 0, color: '#1f2937' }}>
            {selectedDocumentForPreview.file_name}
          </h3>
          <button
            onClick={closeDocumentPreviewModal}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ✕
          </button>
        </div>

        {/* Preview Modal Content */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8f9fa'
        }}>
          {previewLoading ? (
            <div style={{ textAlign: 'center', color: '#666' }}>
              <div style={{ fontSize: '2rem', marginBottom: '10px' }}>⟳</div>
              <p>Loading document...</p>
            </div>
          ) : previewUrl ? (
            <>
              {selectedDocumentForPreview.mime_type.includes('pdf') ? (
                <iframe
                  src={previewUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    minHeight: '500px'
                  }}
                  title="PDF Viewer"
                />
              ) : selectedDocumentForPreview.mime_type.includes('image') ? (
                <img
                  src={previewUrl}
                  alt={selectedDocumentForPreview.file_name}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                />
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: '1.1rem', color: '#666', marginBottom: '20px' }}>
                    Preview not available for this file type
                  </p>
                  <button
                    onClick={() => handleDocumentDownload(selectedDocumentForPreview.id, selectedDocumentForPreview.file_name)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: 'var(--dp-accent-primary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.95rem'
                    }}
                  >
                    ⬇️ Download File
                  </button>
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', color: '#d32f2f' }}>
              <p>Failed to load document preview</p>
            </div>
          )}
        </div>

        {/* Preview Modal Footer */}
        <div style={{
          padding: '16px 20px',
          borderTop: '1px solid #e9ecef',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '10px'
        }}>
          <button
            onClick={() => handleDocumentDownload(selectedDocumentForPreview.id, selectedDocumentForPreview.file_name)}
            style={{
              padding: '8px 16px',
              backgroundColor: 'var(--dp-accent-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            ⬇️ Download
          </button>
          <button
            onClick={closeDocumentPreviewModal}
            style={{
              padding: '8px 16px',
              backgroundColor: '#e5e7eb',
              color: '#1f2937',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  // Render using React Portal
  return ReactDOM.createPortal(
    <>
      {modalContent}
      {documentPreviewModal}
    </>,
    document.getElementById('modal-root') || document.body
  );
};

export default ApprovalModal;
